import { ExaminationDiagnosisMode, VisitExaminationPageModel } from '../models';
import { extractFinishedPhysicalExaminations } from '../utils';
import { fromNullable } from 'fp-ts/es6/Option';
import { getAgeInYears } from '@higo/common/src/utils';
import { extractPatientOverview } from '../utils/extractPatientOverview';
import { patientConfig } from '@higo/common/src/config/patientConfig';
import { Maturity } from '@higo/common/src/types';
import { useEffect, useMemo } from 'react';
import { useGetVisitWithExams } from '@higo/common/src/features/ExaminationPage/hooks/queries/useGetVisitWithExams';
import {
  useUserAnsweredAnamnesisSurvey,
  useUserAnsweredGeneralSurvey,
} from '@higo/common/src/features/FilledSurvey/hooks/useUserAnsweredSurveyQuery';

export const useVisitExaminationPageModel = (
  id: number,
  profileUserId?: number,
): VisitExaminationPageModel | undefined => {
  const {
    data: examinationDetails,
    isLoading: isDetailsLoading,
    refetch,
  } = useGetVisitWithExams(id);

  const patientAgeInYears = examinationDetails?.patient.dateOfBirth
    ? getAgeInYears(examinationDetails?.patient.dateOfBirth)
    : NaN;

  const { data: answeredGeneralSurvey, isLoading: isGeneralSurveyLoading } =
    useUserAnsweredGeneralSurvey(
      examinationDetails?.generalPatientSurveyId ?? NaN,
      patientAgeInYears,
      examinationDetails?.patient.gender,
    );

  const { data: answeredAnamnesisSurvey, isLoading: isAnamnesisSurveyLoading } =
    useUserAnsweredAnamnesisSurvey(
      examinationDetails?.anamnesisPatientSurveyId ?? NaN,
      patientAgeInYears,
      examinationDetails?.examination?.surveyLang,
      examinationDetails?.patient.gender,
    );

  useEffect(() => {
    refetch();
  }, []);

  return useMemo(() => {
    if (
      isDetailsLoading ||
      isGeneralSurveyLoading ||
      isAnamnesisSurveyLoading
    ) {
      return undefined;
    }
    refetch();

    const completedPhysicalExaminations = new Set(
      extractFinishedPhysicalExaminations(
        examinationDetails!.physicalExaminationDetails ?? [],
      ),
    );

    const patient = {
      gender: examinationDetails!.patient.gender,
      maturity:
        patientAgeInYears <= patientConfig.maturityAgeLimits.baby
          ? Maturity.Baby
          : patientAgeInYears <= patientConfig.maturityAgeLimits.teen
          ? Maturity.Teen
          : Maturity.Adult,
      dateOfBirth:
        examinationDetails!.patient.dateOfBirth &&
        new Date(examinationDetails!.patient.dateOfBirth),
    };

    return {
      mode: examinationDetails!.patient.type,
      details: examinationDetails!,
      examinations: examinationDetails!.examinations,
      answeredGeneralSurvey,
      answeredAnamnesisSurvey,
      patient,
      refetch,
      isPhysicalExaminationCompletedFn: (x: any) =>
        completedPhysicalExaminations.has(x),
      patientOverview: extractPatientOverview(
        examinationDetails!.patient,
        fromNullable(answeredGeneralSurvey),
      ),
      diagnosis: {
        mode: ExaminationDiagnosisMode.Editable,
        isEditable: true,
        isReadOnly: false,
        isNotAvailable: false,
      },
    };
  }, [
    isDetailsLoading,
    isGeneralSurveyLoading,
    examinationDetails,
    profileUserId,
    patientAgeInYears,
    answeredGeneralSurvey,
    isAnamnesisSurveyLoading,
    refetch,
  ]);
};
